// FIXED SiteDataContext with natural sorting for numbered images
import React, { createContext, useContext, useState, useEffect } from 'react';

const SiteDataContext = createContext();

// Every cache is namespaced by the build, so deploying a new version makes the
// previous version's entries unreachable rather than serving them as fresh.
const BUILD_ID = process.env.GATSBY_BUILD_ID || 'dev';
const IMAGE_CACHE_PREFIX = 'site-images-cache';
const IMAGE_CACHE_NAME = `${IMAGE_CACHE_PREFIX}-${BUILD_ID}`;

// localStorage keys are versioned the same way.
const cacheKey = (name) => `${name}__${BUILD_ID}`;

// Drop anything left behind by an older build: image caches under our prefix
// that are not the current one, and versioned/legacy localStorage entries.
const purgeStaleCaches = async () => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;
      const isOurs =
        key.startsWith('projects_cache') ||
        key.startsWith('vita_cache') ||
        key.startsWith('news_cache');
      if (isOurs && !key.endsWith(`__${BUILD_ID}`)) {
        localStorage.removeItem(key);
      }
    }
  } catch (err) {
    console.warn('Could not purge old localStorage entries:', err);
  }

  try {
    if (!window.caches) return;
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((name) => name.startsWith(IMAGE_CACHE_PREFIX) && name !== IMAGE_CACHE_NAME)
        .map((name) => caches.delete(name))
    );
  } catch (err) {
    console.warn('Could not purge old image caches:', err);
  }
};

// Helper function to cache an image
const cacheImage = async (url) => {
  if (!url || !url.trim() || url.startsWith('data:') || !window.caches) return;
  
  try {
    const cache = await caches.open(IMAGE_CACHE_NAME);
    const cachedResponse = await cache.match(url);
    
    if (!cachedResponse) {
      console.log("Caching image:", url);
      const response = await fetch(url, { mode: 'no-cors' });
      await cache.put(url, response.clone());
    }
  } catch (err) {
    console.warn(`Failed to cache image ${url}:`, err);
  }
};

// Helper function to cache multiple images
const cacheImages = async (urls) => {
  if (!Array.isArray(urls) || !window.caches) return;
  return Promise.allSettled(urls.map(url => cacheImage(url)));
};

// Helper function to sort files naturally (handles numbered files properly)
const naturalSort = (a, b) => {
  const aName = a.name || a.filename || a.id || '';
  const bName = b.name || b.filename || b.id || '';
  
  return aName.localeCompare(bName, undefined, {
    numeric: true,
    sensitivity: 'base'
  });
};

const loadingImages = new Set(); // Track currently loading images

// Natural size of every thumbnail we have preloaded. The grid is not rendered
// until preloading finishes, so by the time a card first renders its aspect
// ratio is already known here and can be applied during that render.
//
// Without this each card re-measured its own image asynchronously. Chrome
// happened to resolve that in time for the first paint; Safari did not, so
// every card laid out at the fallback 145% ratio and the wide ones snapped to
// 72% a beat later, which reshuffled the columns. Safari also reports
// img.complete === false for an image it already has cached, so the same race
// blanked the thumbnails on the way back from a project page.
const imageDimensions = new Map();

export const getImageDimensions = (url) => (url ? imageDimensions.get(url) || null : null);

export const isImageAlreadyLoaded = (url) => Boolean(url && imageDimensions.has(url));

const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    if (!url || url.trim() === '') {
      return resolve();
    }
    
    // Already measured in this page - nothing left to do.
    if (imageDimensions.has(url)) {
      return resolve(url);
    }

    // Check if this image is already loading
    if (loadingImages.has(url)) {
      return resolve(url);
    }
    
    loadingImages.add(url);

    const img = new Image();
    img.crossOrigin = "anonymous";
    
    img.onload = () => {
      loadingImages.delete(url);
      if (img.naturalWidth && img.naturalHeight) {
        imageDimensions.set(url, { width: img.naturalWidth, height: img.naturalHeight });
      }
      resolve(url);
    };
    img.onerror = () => {
      loadingImages.delete(url);
      console.warn(`Failed to preload image: ${url}`);
      resolve(url); // Resolve anyway to continue loading others
    };
    img.src = url;
  });
};

export const SiteDataProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectImages, setProjectImages] = useState({});
  const [vita, setVita] = useState([]);
  const [news, setNews] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [firstThreeImagesLoaded, setFirstThreeImagesLoaded] = useState(false);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);
  const [arbeitenImagesLoaded, setArbeitenImagesLoaded] = useState(false);

  // Function to preload all project images
  const preloadAllProjectImages = async (projectsData) => {
    try {
      console.log("Preloading images...");
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 480;
      
      // Collect all unique URLs - FIXED: Use consistent priority as ProjectCard
      const uniqueUrls = new Set();
      projectsData.forEach(project => {
        // Priority: thumbnailUrl first, then mediumUrl for mobile (matches ProjectCard)
        if (project.thumbnailUrl) {
          uniqueUrls.add(project.thumbnailUrl);
        } else if (isMobile && project.mediumUrl) {
          uniqueUrls.add(project.mediumUrl);
        }
      });

      // Decode every thumbnail, cached or not. A card cannot lay itself out
      // until it knows its aspect ratio, and being in Cache Storage does not
      // give us naturalWidth - the image still has to be decoded once in this
      // page. Skipping cached URLs here meant that on every repeat visit no
      // dimensions were recorded at all, so the grid laid out at the fallback
      // ratio and then visibly re-sorted once each card measured itself.
      const allUrls = [...uniqueUrls];
      const batchSize = 5;
      for (let i = 0; i < allUrls.length; i += batchSize) {
        const batch = allUrls.slice(i, i + batchSize);
        await Promise.allSettled(batch.map(url => preloadImage(url)));
        // cacheImage() already skips anything that is stored.
        await cacheImages(batch);
      }

      setFirstThreeImagesLoaded(true);
      setArbeitenImagesLoaded(true);
      setAllImagesLoaded(true);

    } catch (err) {
      console.error("Error preloading images:", err);
      setFirstThreeImagesLoaded(true);
      setArbeitenImagesLoaded(true);
      setAllImagesLoaded(true);
    }
  };

  // Fetch projects from our API
  const fetchProjects = async () => {
    try {
      setLoading(true);
      
      // Check local cache first
      const cachedData = localStorage.getItem(cacheKey('projects_cache'));
      const cachedTimestamp = localStorage.getItem(cacheKey('projects_cache_timestamp'));
      
      let parsedData = null;
      
      // Check if cache exists and is valid
      if (cachedData && cachedTimestamp) {
        const now = Date.now();
        const cacheAge = now - parseInt(cachedTimestamp);
        
        // Use cache if it's less than 5 minutes old
        if (cacheAge < 300000) {
          parsedData = JSON.parse(cachedData);
          setProjects(parsedData);
        }
      }
      
      // Always fetch fresh data from our API
      try {
        console.log('Fetching from /api/portfolio...');
        const response = await fetch('/api/portfolio', {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        console.log('Portfolio API response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const apiData = await response.json();
        console.log('Portfolio API data:', apiData);
        
        if (!Array.isArray(apiData)) {
          console.error('API returned non-array data:', apiData);
          throw new Error('Invalid data format from API');
        }
        
        // Transform the API data to match your existing structure
        const transformedData = apiData.map(project => {
          console.log('Processing project:', project.title, 'order:', project.order); // Debug log
          return {
            id: project.id,
            title: project.title,
            year: project.year.toString(),
            category: project.category,
            category2: project.category2 || '', // Include second category
            slug: project.title
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, ''),
            description: project.description || '',
            description2: project.description2 || '',
            description3: project.description3 || '',
            // Use the thumbnail URL directly from API - don't modify it
            thumbnailUrl: project.thumbnail || '',
            mediumUrl: project.thumbnail ? project.thumbnail.replace('/thumb_', '/medium_') : '',
            imageId: project.id, // Use project ID as image reference
            image: project.thumbnail || '',
            // Add width/height if available in project metadata for better aspect ratio detection
            width: project.width || null,
            height: project.height || null,
            // IMPORTANT: Include order field for custom sorting
            order: project.order,
            created: project.created
          };
        });
        
        // FIXED: Sort by order first, then fall back to year/creation date (matches admin panel)
        const sortedData = transformedData.sort((a, b) => {
          // First sort by order (if both have order)
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          // If only one has order, prioritize it
          if (a.order !== undefined) return -1;
          if (b.order !== undefined) return 1;
          
          // Fallback to year descending, then by creation date
          if (b.year !== a.year) {
            return Number(b.year) - Number(a.year);
          }
          if (a.created && b.created) {
            return new Date(b.created) - new Date(a.created);
          }
          // Final fallback to ID
          return Number(b.id) - Number(a.id);
        });
        
        console.log('Transformed and sorted data with order:', sortedData);

        // Only update if data has changed
        const currentProjectsJSON = JSON.stringify(projects);
        const newProjectsJSON = JSON.stringify(sortedData);
        
        if (currentProjectsJSON !== newProjectsJSON) {
          setProjects(sortedData);
          
          // Update cache
          localStorage.setItem(cacheKey('projects_cache'), JSON.stringify(sortedData));
          localStorage.setItem(cacheKey('projects_cache_timestamp'), Date.now().toString());
        }

        // Preload images
        await preloadAllProjectImages(sortedData);
        
      } catch (apiError) {
        console.error('API error in fetchProjects:', apiError);
        
        // Use cache data if available
        if (parsedData) {
          setProjects(parsedData);
          await preloadAllProjectImages(parsedData);
        } else {
          throw apiError;
        }
      }
      
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err.message || 'Failed to fetch projects');
      
      // Try to load from cache as fallback
      try {
        const cachedData = localStorage.getItem(cacheKey('projects_cache'));
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);
          setProjects(parsedData);
          await preloadAllProjectImages(parsedData);
        }
      } catch (cacheErr) {
        console.error('Failed to load fallback cache:', cacheErr);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch project images/videos for detail view
  const fetchProjectImages = async (projectId, shouldCache = false) => {
  try {
    // Check if we already have the media cached in state
    if (projectImages[projectId]) {
      return projectImages[projectId];
    }

    // Check session storage cache
    const cachedMedia = sessionStorage.getItem(`project_media_${projectId}`);
    if (cachedMedia) {
      const mediaData = JSON.parse(cachedMedia);
      setProjectImages(prev => ({
        ...prev,
        [projectId]: mediaData
      }));
      return mediaData;
    }

    // Fetch project details from our API
    const response = await fetch(`/api/portfolio?project=${projectId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch project details: ${response.status}`);
    }

    const projectData = await response.json();
    
    // Transform files to match your existing structure
    const mediaData = (projectData.files || []).map(file => {
      if (file.type === 'image') {
        return {
          id: file.filename,
          type: 'image',
          name: file.originalName || file.filename,
          mimeType: file.mimeType,
          thumbnailUrl: file.thumb,
          mediumUrl: file.medium,
          largeUrl: file.large,
          width: file.width || 0,
          height: file.height || 0,
          isWide: file.width > file.height * 1.2,
          isVideoThumbnail: file.isVideoThumbnail || false,
          videoParentFilename: file.videoParentFilename || null
        };
      } else if (file.type === 'video') {
        return {
          id: file.filename,
          type: 'video',
          name: file.originalName || file.filename,
          mimeType: file.mimeType || 'video/mp4',
          videoUrl: file.url,
          r2Url: file.url,
          url: file.url, // Ensure url property exists
          videoThumbnailUrl: file.thumb || '',
          thumb: file.thumb || '', // Include thumb for consistency
          width: file.width || null, // Default dimensions if not provided
          height: file.height || null,
          duration: file.duration || 0,
          fileSize: file.size || 0,
          isWide: (file.width || 1920) > (file.height || 1080) * 1.2,
          isPending: file.isPending || false,
          manualUploadRequired: file.manualUploadRequired || false
        };
      }
      // Handle any other file types
      return {
        id: file.filename,
        type: file.type || 'unknown',
        name: file.originalName || file.filename,
        ...file
      };
    });

    console.log('Fetched media data for project', projectId, ':', mediaData);

    // Sort media naturally to handle numbered files properly (1.png, 2.png, etc.)
    const sortedMedia = [...mediaData].sort(naturalSort);

    console.log('Original media order:', mediaData.map(img => img.name || img.id));
    console.log('Natural sorted media order:', sortedMedia.map(img => img.name || img.id));

    // Cache the sorted media metadata
    setProjectImages(prev => ({
      ...prev,
      [projectId]: sortedMedia
    }));

    sessionStorage.setItem(`project_media_${projectId}`, JSON.stringify(sortedMedia));

    // Cache images if requested
    if (shouldCache) {
      const imagesToCache = sortedMedia
        .filter(item => item.type === 'image' && item.mediumUrl && !item.isVideoThumbnail)
        .map(item => item.mediumUrl);
      
      if (imagesToCache.length > 0) {
        await cacheImages(imagesToCache);
      }
    }

    return sortedMedia;
  } catch (err) {
    console.error(`Error fetching media for project ${projectId}:`, err);
    throw err;
  }
};
  // Fetch vita data from our API
  const fetchVitaData = async () => {
    try {
      console.log('Fetching vita data from API...');
      
      // Paint from cache straight away so the page is not blank, but always
      // revalidate: the old code returned early for 10 minutes, so a reordered
      // CV could take that long to show up.
      const cachedVita = localStorage.getItem(cacheKey('vita_cache'));

      if (cachedVita) {
        try {
          setVita(JSON.parse(cachedVita));
        } catch (parseErr) {
          console.warn('Ignoring unreadable vita cache:', parseErr);
        }
      }

      const response = await fetch('/api/portfolio?type=vita', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const vitaData = await response.json();
        console.log('Vita data from API:', vitaData);
        setVita(vitaData);
        
        // Cache the data
        localStorage.setItem(cacheKey('vita_cache'), JSON.stringify(vitaData));
        localStorage.setItem(cacheKey('vita_cache_timestamp'), Date.now().toString());
      } else {
        console.warn('Failed to fetch vita data, using empty array');
        setVita([]);
      }
    } catch (err) {
      console.error('Error fetching vita:', err);
      setVita([]);
    }
  };

  // Fetch news data from our API
  const fetchCurrentData = async () => {
    try {
      console.log('Fetching news data from API...');
      
      // Same as vita: show the cached copy immediately, then always refetch.
      const cachedNews = localStorage.getItem(cacheKey('news_cache'));

      if (cachedNews) {
        try {
          setNews(JSON.parse(cachedNews));
        } catch (parseErr) {
          console.warn('Ignoring unreadable news cache:', parseErr);
        }
      }

      const response = await fetch('/api/portfolio?type=news', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const newsData = await response.json();
        // Optionally, migrate old entries with only "date" to "startDate"
        const normalizedNews = newsData.map(item => ({
          ...item,
          startDate: item.startDate || item.date || '',
          endDate: item.endDate || '',
        }));
        setNews(normalizedNews);
        
        // Cache the data
        localStorage.setItem(cacheKey('news_cache'), JSON.stringify(newsData));
        localStorage.setItem(cacheKey('news_cache_timestamp'), Date.now().toString());
      } else {
        console.warn('Failed to fetch news data, using empty array');
        setNews([]);
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setNews([]);
    }
  };

  // Fetch profile image from our API
  const fetchProfileImage = async () => {
    try {
      console.log('Fetching profile image from API...');
      
      const response = await fetch('/api/portfolio?type=profile-image', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const imageData = await response.json();
        console.log('Profile image data from API:', imageData);
        setProfileImage(imageData);
      } else {
        console.warn('No profile image found');
        setProfileImage(null);
      }
    } catch (err) {
      console.error('Error fetching profile image:', err);
      setProfileImage(null);
    }
  };

  // Refresh functions to maintain compatibility with your existing code
  const refreshDriveImages = async () => {
    try {
      // Clear caches
      localStorage.removeItem(cacheKey('projects_cache'));
      localStorage.removeItem(cacheKey('projects_cache_timestamp'));
      localStorage.removeItem(cacheKey('vita_cache'));
      localStorage.removeItem(cacheKey('vita_cache_timestamp'));
      localStorage.removeItem(cacheKey('news_cache'));
      localStorage.removeItem(cacheKey('news_cache_timestamp'));
      setProjectImages({});
      sessionStorage.clear();
      
      if (window.caches) {
        await caches.delete(IMAGE_CACHE_NAME);
      }
      
      // Reload all data
      await Promise.all([
        fetchProjects(),
        fetchVitaData(),
        fetchCurrentData(),
        fetchProfileImage()
      ]);
      return true;
    } catch (err) {
      console.error('Error refreshing data:', err);
      throw err;
    }
  };

  const refreshSheetsData = async () => {
    return refreshDriveImages(); // Same operation now
  };

  const refreshAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchProjects(),
        fetchVitaData(),
        fetchCurrentData(),
        fetchProfileImage()
      ]);
      return true;
    } catch (err) {
      console.error('Error refreshing all data:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      setAllImagesLoaded(false);
      
      await purgeStaleCaches();

      try {
        await Promise.all([
          fetchProjects(),
          fetchVitaData(),
          fetchCurrentData(),
          fetchProfileImage()
        ]);
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    loadAllData();
  }, []);

  return (
    <SiteDataContext.Provider
      value={{
        projects,
        vita,
        news,
        profileImage,
        loading,
        error,
        allImagesLoaded,
        arbeitenImagesLoaded,
        firstThreeImagesLoaded,
        fetchProjectImages,
        refreshDriveImages,
        refreshSheetsData,
        refreshAllData
      }}
    >
      {children}
    </SiteDataContext.Provider>
  );
};

export const useSiteData = () => useContext(SiteDataContext);